import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { DateTimePicker } from '../ui/date-time-picker';
import ConfirmDialog from '../ui/confirm-dialog';
import {
  isInvitationOpen,
  toDatetimeLocalValue,
  updateInvitationClosure,
} from '../../lib/invitation';
import { toast } from '../../lib/toast';

function InvitationClosureDialog({ open, onOpenChange, invitation, onSaved }) {
  const [isOpen, setIsOpen] = useState(true);
  const [closesAt, setClosesAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    if (!open || !invitation) return;
    setIsOpen(invitation.isOpen !== false);
    setClosesAt(toDatetimeLocalValue(invitation.closesAt));
    setError('');
  }, [open, invitation]);

  const save = async (overrides = {}) => {
    if (!invitation?.id) return;
    const payload = {
      isOpen: overrides.isOpen ?? isOpen,
      closesAt: overrides.closesAt !== undefined ? overrides.closesAt : (closesAt || null),
    };

    setSaving(true);
    setError('');
    try {
      await updateInvitationClosure(invitation.id, payload);
      onSaved?.(invitation.id, payload);
      onOpenChange(false);
      if (overrides.isOpen === false) {
        toast.success('Iscrizioni chiuse', 'L\'invito non accetta più nuove risposte.');
      } else if (overrides.isOpen === true) {
        toast.success('Iscrizioni riaperte', 'I partecipanti possono inviare nuove risposte.');
      } else {
        toast.success('Impostazioni salvate', 'Apertura e chiusura iscrizioni aggiornate.');
      }
    } catch {
      setError('Impossibile aggiornare lo stato dell\'invito. Riprova.');
      toast.error('Aggiornamento non riuscito', 'Impossibile modificare lo stato dell\'invito.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseNow = () => {
    setConfirmAction('close');
  };

  const handleReopen = () => {
    setConfirmAction('reopen');
  };

  const handleSaveClick = () => {
    setConfirmAction('save');
  };

  const executeConfirm = async () => {
    if (confirmAction === 'close') {
      setIsOpen(false);
      await save({ isOpen: false });
    } else if (confirmAction === 'reopen') {
      setIsOpen(true);
      await save({ isOpen: true });
    } else if (confirmAction === 'save') {
      await save();
    }
  };

  const confirmCopy = {
    close: {
      title: 'Chiudere le iscrizioni?',
      description: 'L\'invito non accetterà più nuove risposte finché non lo riapri.',
      confirmLabel: 'Chiudi iscrizioni',
      variant: 'destructive',
    },
    reopen: {
      title: 'Riaprire le iscrizioni?',
      description: 'I partecipanti potranno di nuovo inviare risposte tramite il link dell\'invito.',
      confirmLabel: 'Riapri',
    },
    save: {
      title: 'Salvare le impostazioni?',
      description: 'Le modifiche ad apertura e chiusura iscrizioni verranno applicate.',
      confirmLabel: 'Salva',
    },
  };

  const activeConfirm = confirmAction ? confirmCopy[confirmAction] : null;

  const currentlyOpen = invitation ? isInvitationOpen(invitation) : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Gestisci iscrizioni
          </DialogTitle>
          <DialogDescription>
            {invitation?.name
              ? `Imposta apertura e chiusura per "${invitation.name}" senza modificare campi o contenuti.`
              : 'Imposta apertura e chiusura iscrizioni.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-[var(--ba-surface-border)] px-3 py-2 text-sm">
            Stato attuale:{' '}
            <strong className={currentlyOpen ? 'text-brand-green' : 'text-brand-gold'}>
              {currentlyOpen ? 'Aperto' : 'Chiuso'}
            </strong>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-orange"
              checked={isOpen}
              onChange={(e) => setIsOpen(e.target.checked)}
              disabled={saving}
            />
            Invito aperto alle iscrizioni
          </label>

          <div>
            <label className="ba-label" htmlFor="closure-closes-at">
              Data chiusura iscrizioni (opzionale)
            </label>
            <DateTimePicker
              id="closure-closes-at"
              value={closesAt}
              onChange={setClosesAt}
              allowOptionalTime={false}
              disabled={saving}
              placeholder="Seleziona data e ora di chiusura…"
            />
            <p className="mt-1 ba-muted">
              Dopo questa data il modulo non accetterà nuove risposte, anche se l&apos;invito risulta aperto.
            </p>
          </div>

          {error && <p className="text-sm text-brand-danger">{error}</p>}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {currentlyOpen ? (
              <Button type="button" variant="destructive" disabled={saving} onClick={handleCloseNow}>
                Chiudi ora
              </Button>
            ) : (
              <Button type="button" variant="outline" disabled={saving} onClick={handleReopen}>
                Riapri iscrizioni
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="button" disabled={saving} onClick={handleSaveClick}>
              {saving ? 'Salvataggio…' : 'Salva'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title={activeConfirm?.title}
        description={activeConfirm?.description}
        confirmLabel={activeConfirm?.confirmLabel}
        variant={activeConfirm?.variant}
        onConfirm={executeConfirm}
      />
    </Dialog>
  );
}

export default InvitationClosureDialog;
